import moment from 'moment';
import strings from 'strings';
import { DateFormats, MONTHS } from 'UI/constants/defaults';

const formatDate = (dateObj, isCurrent = false) => {
  if (isCurrent) return strings.resumeSearch.resumeCard.currentYear;
  if (!dateObj) return '';

  if (dateObj.month && dateObj.year) {
    const month = MONTHS.find(m => m.id === dateObj.month);
    return `${month?.title || ''} ${dateObj.year}`;
  }
  if (dateObj.year) {
    return `${dateObj.year}`;
  }
  return '';
};

export const getPeriodString = (startDate, endDate, isCurrent = false) => {
  const start = formatDate(startDate);
  const end = formatDate(endDate, isCurrent);
  return [start, end].filter(Boolean).join(' - ');
};

export const formatEducationPeriod = (startYear, endYear) => {
  if (startYear && endYear) {
    return `${startYear} - ${endYear}`;
  }
  return endYear || startYear || '';
};

export const formatPluralMessage = (message, count) => {
  const pluralSuffix = count > 1 ? 's' : '';
  return message.replace('{count}', count).replace('{plural}', pluralSuffix);
};

export const formatDescription = text => text || '';

export const extractLocation = (basics = {}) => {
  const address = basics.address_components || {};
  if (address.found_match) {
    return `${address.city_title}, ${address.state_slug}`;
  }
  return basics.address || '';
};

export const extractFullName = (basics = {}) => {
  const firstName = basics.first_name || '';
  const lastName = basics.last_name || '';
  return `${firstName} ${lastName}`.trim();
};

export const extractProfession = (basics = {}) => basics.profession || '';

export const extractExperiences = (profile = {}) => profile.professional_experiences || [];

export const extractEducations = (profile = {}) => profile.educations || [];

export const extractCertifications = (profile = {}) => profile.certifications_and_licenses || [];

export const extractAssociations = (profile = {}) =>
  profile.professional_associations_and_memberships || [];

export const extractSkills = (profile = {}) => profile.skills || [];

export const extractPhoneNumbers = (basics = {}) => basics.phone_numbers || [];

export const extractEmails = (basics = {}) => basics.emails || [];

export const getTypeColor = (type, colors = {}) => colors[type];

export const getStatusColor = (status, colors = {}) => colors[status];

export const formatMetadataDate = isoDate => {
  if (!isoDate) {
    return '—';
  }

  const date = moment(isoDate);

  if (!date.isValid()) {
    return '—';
  }

  return date.format(DateFormats.MonthDayYear);
};

export const extractCandidateType = resume => resume?.candidate?.type || null;

export const extractCandidateStatus = resume => resume?.candidate?.status || null;

export const extractCreatedAt = resume => resume?.meta?.file?.uploadedAt || null;

export const extractLastActivity = resume => resume?.candidate?.last_activity_date || null;

const extractFunctionalTitle = resume => {
  return resume?.candidate?.functionalTitle?.title || '';
};

const extractIndustry = resume => {
  return resume?.candidate?.industry?.title || '';
};

const extractSpecialty = resume => {
  return resume?.candidate?.specialty?.title || '';
};

const extractSubSpecialty = resume => {
  return resume?.candidate?.subspecialty?.title || '';
};

export const extractResumeData = (resume = {}) => {
  const profile = resume.profile || {};
  const basics = profile.basics || {};
  const candidateType = extractCandidateType(resume);
  const candidateStatus = extractCandidateStatus(resume);

  return {
    fullName: extractFullName(basics),
    profession: extractProfession(basics),
    functionalTitle: extractFunctionalTitle(resume),
    industry: extractIndustry(resume),
    specialty: extractSpecialty(resume),
    subSpecialty: extractSubSpecialty(resume),
    location: extractLocation(basics),
    experiences: extractExperiences(profile),
    educations: extractEducations(profile),
    certifications: extractCertifications(profile),
    associations: extractAssociations(profile),
    skills: extractSkills(profile),
    phoneNumbers: extractPhoneNumbers(basics),
    emails: extractEmails(basics),
    candidateType: candidateType ? { id: candidateType.id, title: candidateType.title } : null,
    candidateStatus: candidateStatus
      ? { id: candidateStatus.id, title: candidateStatus.title }
      : null,
    createdAt: extractCreatedAt(resume),
    lastActivity: extractLastActivity(resume)
  };
};

export const getExpandButtonText = ({ items = [] }) => {
  return strings.formatString(strings.resumeSearch.resumeCard.showAll, {
    count: items.length
  });
};
